import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface PayrollItem {
  id?: string;
  line_number: number;
  employee_id: string;
  salary_amount?: string;
  bonus_amount?: string;
  // Ручне доп-нарахування (колонка «Додати»). bonus_amount = надбавки з наказу
  // (сервер, read-only); manual_amount = ручний ввід. Обидва входять у gross.
  manual_amount?: string;
  advance_amount?: string;
  norm_days?: string;
  working_days?: string;
  norm_hours?: string | null;
  worked_hours?: string | null;
  calc_type_code?: string | null;  // SALARY/HOURLY — для коректного перерахунку gross
  timesheet_id?: string | null;
  gross_amount: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
  // Iндивідуальний ЄСВ для підказки: ставка (8.41% інвалід / 22%), база
  // (мінбаза-доначислення/кап) + прапор капа. Приходять з Out + serverCalc.
  esv_rate_individual?: string;
  esv_base_amount?: string;
  esv_base_capped?: boolean;
  net_amount: string;
  cost_item_id?: string | null;
  comment?: string | null;
  // Block 9 (2026-05-27): ПСП snapshot
  psp_category?: string | null;
  psp_amount?: string;
  psp_threshold?: string;
  psp_applied?: boolean;
  // TD-B-P5.1.2 (2026-06-01): прапор одноразової мат.допомоги.
  // При true — item виключається з чисельника середнього заробітку
  // у vacation/sick розрахунках.
  is_one_time_aid?: boolean;
  // Обгортка VACATION/SICK (рядок-вікно на наказ/розрахунок).
  source_kind?: 'VACATION' | 'SICK' | null;
  source_order_id?: string | null;
  source_calc_id?: string | null;
  average_daily_wage?: string | null;
  days_count?: number | null;
  needs_order_hint?: string | null;
}

export interface PayrollListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  payroll_kind?: 'MAIN' | 'ADVANCE' | 'VACATION' | 'SICK' | 'CIVIL';
  total_gross: string;
  total_net: string;
  posted: boolean;
  deletion_mark: boolean;
  project_id?: string | null;
  // Block 5.3f:
  state?: CancellableState;
  external_source?: string | null;
}

export interface Payroll extends PayrollListItem {
  expense_account_code: string;
  payroll_account_code: string;
  pdfo_account_code: string;
  wz_account_code: string;
  esv_account_code: string;
  pdfo_rate: string;
  wz_rate: string;
  esv_rate: string;
  advance_percent?: string;
  total_pdfo: string;
  total_wz: string;
  total_esv: string;
  posted_at: string | null;
  comment: string | null;
  items: PayrollItem[];
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface PayrollCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  payroll_kind?: 'MAIN' | 'ADVANCE' | 'VACATION' | 'SICK' | 'CIVIL';
  advance_percent?: string;
  expense_account_code?: string;
  payroll_account_code?: string;
  pdfo_account_code?: string;
  wz_account_code?: string;
  esv_account_code?: string;
  pdfo_rate?: string;
  wz_rate?: string;
  esv_rate?: string;
  comment?: string | null;
  items: PayrollItem[];
  project_id?: string | null;
}

export type PayrollUpdate = Partial<PayrollCreate>;

// Block 2.3: backend-authoritative preview.
export interface PayrollCalculatePreviewItem {
  employee_id: string;
  salary_amount: string;
  bonus_amount: string;
  manual_amount: string;
  advance_amount: string;
  gross_amount: string;
  pdfo_rate: string;
  wz_rate: string;
  esv_rate: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_base_amount: string;
  esv_amount: string;
  esv_base_capped: boolean;
  net_amount: string;
  rates_used_date: string;
  cost_item_id: string | null;
  expense_account_code: string;
  norm_days: number;
  working_days: number;
  norm_hours: string | null;
  worked_hours: string | null;
  calc_type_code: string | null;
  timesheet_id: string | null;
  source_kind?: 'VACATION' | 'SICK' | null;
  source_order_id?: string | null;
  average_daily_wage?: string | null;
  days_count?: number | null;
  needs_order_hint?: string | null;
}

export interface PayrollCalculatePreviewResult {
  organization_id: string;
  period_year: number;
  period_month: number;
  items: PayrollCalculatePreviewItem[];
}

export const payrolls = {
  list: (params?: {
    organization_id?: string; period_year?: number; period_month?: number;
    payroll_kind?: 'MAIN' | 'ADVANCE' | 'CIVIL'; date_from?: string; date_to?: string;
    posted?: boolean; limit?: number;
  }) =>
    api.get<Page<PayrollListItem>>('/payrolls', { params }).then((r) => r.data),
  get: (id: string) => api.get<Payroll>(`/payrolls/${id}`).then((r) => r.data),
  create: (payload: PayrollCreate) =>
    api.post<Payroll>('/payrolls', payload).then((r) => r.data),
  update: (id: string, payload: PayrollUpdate) =>
    api.patch<Payroll>(`/payrolls/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/payrolls/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<Payroll>(`/payrolls/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<Payroll>(`/payrolls/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/payrolls/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  calculatePreview: (payload: {
    organization_id: string;
    period_year: number;
    period_month: number;
    employee_ids?: string[];
    payroll_kind?: 'MAIN' | 'ADVANCE' | 'VACATION' | 'SICK' | 'CIVIL';
    advance_percent?: string;
    // Ручне «Додати» per-employee — входить у gross при розрахунку.
    manual_amounts?: Record<string, string>;
  }) =>
    api.post<PayrollCalculatePreviewResult>(
      '/payrolls/calculate-preview', payload,
    ).then((r) => r.data),
  printPayslipUrl: (id: string, employee_id: string) =>
    `/api/v1/payrolls/${id}/print-payslip?employee_id=${employee_id}`,
};

// =================== PayrollPayment ===================

export type PayoutMethod = 'CASH' | 'BANK';
// Вид виплати = види НАРАХУВАНЬ. Лікарняний розділено: SICK_ORG (роботодавець) +
// SICK_PFU (Фонд). SICK/SALARY — legacy (не у виборі видів).
export type PayoutKind =
  'MAIN' | 'ADVANCE' | 'VACATION' | 'COMPENSATION' | 'SICK_ORG' | 'SICK_PFU' | 'CIVIL';

export const PAYOUT_KIND_LABELS: Record<PayoutKind, string> = {
  MAIN: 'ЗП основна', ADVANCE: 'Аванс', VACATION: 'Відпустка',
  COMPENSATION: 'Компенсація відпустки',
  SICK_ORG: 'Лікарняний (орг)', SICK_PFU: 'Лікарняний (ПФУ)',
  CIVIL: 'Виплата за ЦПУ',
};

export interface UnpaidAccrualItem {
  employee_id: string;
  employee_name: string;
  amount: string;
  suggested_method: PayoutMethod;
}
export interface UnpaidAccrualsResult { items: UnpaidAccrualItem[]; }

export interface UnpaidDocument {
  id: string;
  document_number: string;
  document_date: string | null;
  total: string;
  employee_name: string | null;  // для одиночних (1 особа); null якщо багато
  employee_count: number;
  items: UnpaidAccrualItem[];
}
export interface UnpaidDocumentsResult { documents: UnpaidDocument[]; }

export interface PayrollPaymentItem {
  id?: string;
  line_number: number;
  employee_id: string;
  amount: string;
  // Спосіб + реквізити виплати per-рядок (Фаза 1.1). NULL → дефолт з шапки.
  payment_method?: PayoutMethod | null;
  cash_register_id?: string | null;
  bank_account_id?: string | null;
  recipient_iban?: string | null;
  recipient_bank_name?: string | null;
  // Override рах. розрахунків рядка (закладка «Рахунки обліку»). NULL → резолвер.
  settlement_account_code?: string | null;
  // Лише фронт (підказка боргу при редагуванні суми). НЕ слати на backend.
  accrued?: string;
}

export interface PayrollPaymentDoc {
  doc_type: string;  // 'cash' | 'bank'
  doc_id: string;
}

export interface PayrollPaymentListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  payment_method: string;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  payout_kind?: string;   // SALARY/ADVANCE/VACATION/SICK — колонка «Вид виплати»
  period_year?: number | null;   // колонка «Період»
  period_month?: number | null;
  // Block 5.3f:
  state?: CancellableState;
  external_source?: string | null;
}

export interface PayrollPayment extends PayrollPaymentListItem {
  payroll_id: string | null;
  payout_kind?: PayoutKind;
  cash_payment_id: string | null;
  bank_payment_id: string | null;
  posted_at: string | null;
  comment: string | null;
  items: PayrollPaymentItem[];
  payment_docs?: PayrollPaymentDoc[];
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface PayrollPaymentCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  payroll_id?: string | null;
  payment_method: PayoutMethod;
  payout_kind?: PayoutKind;
  period_year?: number | null;
  period_month?: number | null;
  comment?: string | null;
  items: PayrollPaymentItem[];
}

export type PayrollPaymentUpdate = Partial<PayrollPaymentCreate>;

export const payrollPayments = {
  list: (params?: {
    organization_id?: string; posted?: boolean; payout_kind?: string;
    period_year?: number; period_month?: number;
    date_from?: string; date_to?: string; limit?: number;
  }) =>
    api.get<Page<PayrollPaymentListItem>>('/payroll-payments', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<PayrollPayment>(`/payroll-payments/${id}`).then((r) => r.data),
  create: (payload: PayrollPaymentCreate) =>
    api.post<PayrollPayment>('/payroll-payments', payload).then((r) => r.data),
  update: (id: string, payload: PayrollPaymentUpdate) =>
    api.patch<PayrollPayment>(`/payroll-payments/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/payroll-payments/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<PayrollPayment>(`/payroll-payments/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<PayrollPayment>(`/payroll-payments/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/payroll-payments/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  // F2: борг по лікарняних для виплати (employer→р/р + fund→спецрахунок).
  sickDebt: (params: { organization_id: string; period_year: number; period_month: number }) =>
    api.get<SickDebtResult>('/payroll-payments/sick-debt', { params }).then((r) => r.data),
  // Неоплачені нарахування обраного виду+періоду (нараховано − виплачено).
  unpaidAccruals: (params: { organization_id: string; payout_kind: PayoutKind;
    period_year: number; period_month: number }) =>
    api.get<UnpaidAccrualsResult>('/payroll-payments/unpaid-accruals', { params }).then((r) => r.data),
  // Проведені неоплачені ДОКУМЕНТИ виду+періоду (для поля «На основі нарахування»).
  unpaidDocuments: (params: { organization_id: string; payout_kind: PayoutKind;
    period_year: number; period_month: number }) =>
    api.get<UnpaidDocumentsResult>('/payroll-payments/unpaid-documents', { params }).then((r) => r.data),
  // B: 6 платіжок податків лікарняних (ПДФО+ВЗ+ЄСВ × роботодавець/Фонд).
  sickTaxDebt: (params: { organization_id: string; period_year: number; period_month: number }) =>
    api.get<SickTaxDebtResult>('/payroll-payments/sick-tax-debt', { params }).then((r) => r.data),
};

export interface SickTaxDebtResult {
  items: Array<{
    funding_source: 'EMPLOYER' | 'FUND';
    tax_type: 'PDFO' | 'WZ' | 'ESV';
    account_code: string;
    amount: string;
    source_account_id: string | null;
  }>;
  main_account_id: string | null;
  special_account_id: string | null;
}

export interface SickDebtResult {
  items: Array<{
    employee_id: string;
    employer_amount: string;
    fund_amount: string;
    total_amount: string;
  }>;
  special_account_id: string | null;
  main_account_id: string | null;
}
